import { GlobalOutlined } from "@ant-design/icons";
import { FormattedMessage } from "react-intl";

import { CandidateAssessmentViewTypes } from "../../CandidateAssessment";
import WelcomeElement from "@/shared/components/candidate/scenes/WelcomeElement/WelcomeElement";
import { SubmitButton } from "@/ui-components/commonComponents/Button/SubmitButton";
import { ContentGatedFooter } from "../ContentGatedFooter/ContentGatedFooter";
import { Button } from "@/ui-components/commonComponents/Button/Button";
import { Loading } from "@/shared/components/Loading/Loading";
import { localeEndonym } from "@/shared/utils/locale.utils";
import { LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";
import AssessmentIntakeForm, { ASSESSMENT_INTAKE_FORM_ID } from "../AssessmentIntakeForm/AssessmentIntakeForm";
import LanguageSelectModal from "../modals/LanguageSelectModal/LanguageSelectModal";
import { useCandidateLocaleSelection } from "../../hooks/useCandidateLocaleSelection.hook";
import { preloadDemographics } from "../AssessmentIntakeForm/demographicsPreload";
import { useIsSurvey } from "@/modules/candidate/assessmentCopy";
import styles from "./style.module.css";

interface AssessmentIntroViewProps {
  viewType: CandidateAssessmentViewTypes;
  title?: string;
  introText?: string;
  leftTime?: string;
  timerDisabled?: boolean;
  buttonTitle: string;
  onSubmit: () => Promise<void>;
  hasAcceptedTerms: boolean;
  inviteId?: number;
  testId?: number;
  demographicsEnabled?: boolean;
  employeeIdEnabled?: boolean;
  requiresIdentityAttestation: boolean;
  demographicsSubmittedAt?: string | null;
  employeeId?: string | null;
  locale?: LanguagesIsoEnum | null;
  // True once the attempt has started; locks the language (hides the "Change" link, no auto-open).
  hasStarted?: boolean;
  // Bubbles the newly persisted locale up on Confirm so the ancestor re-fetches localized content.
  onLocaleConfirmed?: (locale: LanguagesIsoEnum) => void;
  onLocaleSelected?: (locale: LanguagesIsoEnum) => void;
  isContentStale?: boolean;
  hasContentError?: boolean;
  onRetryContent: () => void;
}

// The assessment intro (pre-start) view. Owns the candidate language-selection flow: it gates the
// whole page on the available-languages fetch, renders the "Change Language" modal + a header
// language indicator, and hosts the intake form + start button.
export default function AssessmentIntroView({
  viewType,
  title,
  introText,
  leftTime,
  timerDisabled = false,
  buttonTitle,
  onSubmit,
  hasAcceptedTerms,
  inviteId,
  testId,
  demographicsEnabled = false,
  employeeIdEnabled = false,
  requiresIdentityAttestation,
  demographicsSubmittedAt,
  employeeId,
  locale,
  hasStarted = false,
  onLocaleConfirmed,
  onLocaleSelected,
  isContentStale = false,
  hasContentError = false,
  onRetryContent,
}: AssessmentIntroViewProps) {
  const {
    isLoading,
    error,
    retry,
    availableLocales,
    canChangeLanguage,
    currentLocale,
    isModalOpen,
    selectedLocale,
    selectAndApplyLocale,
    confirm,
    isConfirming,
    isLoadingSelection,
    openModal,
    isApplyingInitialLocale,
  } = useCandidateLocaleSelection({
    inviteId,
    inviteLocale: locale,
    hasStarted,
    onConfirmed: onLocaleConfirmed,
    onSelected: onLocaleSelected,
    isContentStale,
    hasContentError,
    preload:
      inviteId && demographicsEnabled && !demographicsSubmittedAt
        ? (picked) => preloadDemographics(inviteId, picked)
        : undefined,
  });
  const isSurvey = useIsSurvey();

  const isStartBlocked = isContentStale || isApplyingInitialLocale;

  // Page-level gate: the language prerequisite must resolve before the candidate can proceed.
  if (isLoading) {
    return <Loading />;
  }

  // English copy on purpose: the locale-selection prerequisite has failed, so we can't rely on it.
  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorMessage}>
          We couldn't load the language options for this {isSurvey ? "survey" : "assessment"}. Please try again.
        </p>
        <Button onClick={retry}>Try again</Button>
      </div>
    );
  }

  const languageIndicator = currentLocale ? (
    <span className={styles.languageIndicator}>
      <GlobalOutlined aria-hidden="true" />
      <span className={styles.languageLabel}>{localeEndonym(currentLocale)}</span>
      {canChangeLanguage && (
        <button type="button" className={styles.changeLink} onClick={openModal}>
          <FormattedMessage id="candidate.languageSelect.change" defaultMessage="Change" />
        </button>
      )}
    </span>
  ) : null;

  return (
    <>
      <WelcomeElement
        type={viewType}
        title={title}
        description={introText}
        leftTime={leftTime}
        timerDisabled={timerDisabled}
        languageIndicator={languageIndicator}
        footer={
          // Confirm closes the picker the moment the locale is persisted, while the content for that
          // locale is still in flight. Without this the candidate can start into the previous
          // language's questions and watch them swap underneath them a moment later.
          <ContentGatedFooter
            blocked={isStartBlocked}
            isStale={isContentStale}
            hasError={hasContentError}
            onRetry={onRetryContent}
          >
            {({ isLoading, disabled }) => (
              <SubmitButton form={ASSESSMENT_INTAKE_FORM_ID} isLoading={isLoading} disabled={disabled}>
                {buttonTitle}
              </SubmitButton>
            )}
          </ContentGatedFooter>
        }
      >
        <AssessmentIntakeForm
          hasAcceptedTerms={hasAcceptedTerms}
          inviteId={inviteId}
          testId={testId}
          onSubmit={onSubmit}
          demographicsEnabled={demographicsEnabled}
          employeeIdEnabled={employeeIdEnabled}
          requiresIdentityAttestation={requiresIdentityAttestation}
          demographicsSubmittedAt={demographicsSubmittedAt}
          employeeId={employeeId}
        />
      </WelcomeElement>

      <LanguageSelectModal
        open={isModalOpen}
        availableLocales={availableLocales}
        value={selectedLocale}
        onChange={selectAndApplyLocale}
        onConfirm={confirm}
        confirming={isConfirming || isLoadingSelection}
      />
    </>
  );
}
